/* src/pages/SystemSettings.js - System configuration for email and SMS */
import React, { useState, useEffect } from 'react';
import API from '../services/api';
import '../styles/Dashboard.css';

export default function SystemSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [queueStatus, setQueueStatus] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchQueueStatus();
    // Poll queue status every 5 seconds
    const interval = setInterval(fetchQueueStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/system-config');
      setConfig(data);
    } catch (err) {
      showMessage('error', err.response?.data?.msg || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueStatus = async () => {
    try {
      const { data } = await API.get('/system-config/email-queue');
      setQueueStatus(data);
    } catch (err) {
      // Silently fail - queue status is not critical
      console.error('Queue status fetch failed:', err);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { data } = await API.put('/system-config', config);
      setConfig(data.config);
      showMessage('success', 'Configuration saved successfully!');
    } catch (err) {
      showMessage('error', err.response?.data?.msg || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      const { data } = await API.post('/system-config/test-email');
      showMessage('success', data.msg);
    } catch (err) {
      showMessage('error', err.response?.data?.msg || 'Test email failed');
    } finally {
      setTestingEmail(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 18, color: '#6b7280' }}>Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 8px', color: '#1f2937', fontSize: 28 }}>⚙️ System Settings</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
          Configure email and SMS notification settings
        </p>
      </div>

      {message.text && (
        <div style={{
          padding: 16,
          marginBottom: 24,
          borderRadius: 8,
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Email Queue Status */}
      {queueStatus && queueStatus.queueSize > 0 && (
        <div style={{
          background: '#eff6ff',
          padding: 16,
          marginBottom: 24,
          borderRadius: 8,
          border: '2px solid #60a5fa',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}>
          <div style={{ fontSize: 32 }}>📬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>
              Email Queue Active
            </div>
            <div style={{ fontSize: 13, color: '#3b82f6' }}>
              <strong>{queueStatus.queueSize}</strong> email{queueStatus.queueSize !== 1 ? 's' : ''} waiting to be sent
              {queueStatus.processing && (
                <span style={{ marginLeft: 8 }}>
                  • <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>🔄 Processing...</span>
                </span>
              )}
            </div>
            {queueStatus.oldestEmail && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Oldest: {queueStatus.oldestEmail}
              </div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* ========== EMAIL SETTINGS ========== */}
        <div style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: 20 }}>📧 Email Settings</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config?.emailEnabled || false}
                onChange={(e) => handleChange('emailEnabled', e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: config?.emailEnabled ? '#059669' : '#6b7280' }}>
                {config?.emailEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: config?.emailEnabled ? 1 : 0.5 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                SMTP Host
              </label>
              <input
                type="text"
                value={config?.smtpHost || ''}
                onChange={(e) => handleChange('smtpHost', e.target.value)}
                disabled={!config?.emailEnabled}
                style={inputStyle}
                placeholder="smtp.gmail.com"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                SMTP Port
              </label>
              <input
                type="number"
                value={config?.smtpPort || 587}
                onChange={(e) => handleChange('smtpPort', parseInt(e.target.value))}
                disabled={!config?.emailEnabled}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                SMTP User (Email)
              </label>
              <input
                type="email"
                value={config?.smtpUser || ''}
                onChange={(e) => handleChange('smtpUser', e.target.value)}
                disabled={!config?.emailEnabled}
                style={inputStyle}
                placeholder="your-email@gmail.com"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                SMTP Password / App Password
              </label>
              <input
                type="password"
                value={config?.smtpPass || ''}
                onChange={(e) => handleChange('smtpPass', e.target.value)}
                disabled={!config?.emailEnabled}
                style={inputStyle}
                placeholder="Enter new password or leave masked"
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                From Name
              </label>
              <input
                type="text"
                value={config?.emailFromName || ''}
                onChange={(e) => handleChange('emailFromName', e.target.value)}
                disabled={!config?.emailEnabled}
                style={inputStyle}
                placeholder="BNBWU Gate Tracking"
              />
            </div>
          </div>

          {config?.emailEnabled && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={testingEmail}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: testingEmail ? 'not-allowed' : 'pointer',
                  opacity: testingEmail ? 0.6 : 1,
                }}
              >
                {testingEmail ? 'Sending...' : '📤 Send Test Email'}
              </button>
            </div>
          )}
        </div>

        {/* ========== SMS SETTINGS ========== */}
        <div style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: 20 }}>📱 SMS Settings</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config?.smsEnabled || false}
                onChange={(e) => handleChange('smsEnabled', e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: config?.smsEnabled ? '#059669' : '#6b7280' }}>
                {config?.smsEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: config?.smsEnabled ? 1 : 0.5 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                Provider
              </label>
              <select
                value={config?.smsProvider || 'zong'}
                onChange={(e) => handleChange('smsProvider', e.target.value)}
                disabled={!config?.smsEnabled}
                style={inputStyle}
              >
                <option value="zong">Zong</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                SMS Mask
              </label>
              <input
                type="text"
                value={config?.smsMask || ''}
                onChange={(e) => handleChange('smsMask', e.target.value)}
                disabled={!config?.smsEnabled}
                style={inputStyle}
                placeholder="BNBWU-SUK"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                Login ID
              </label>
              <input
                type="text"
                value={config?.smsLoginId || ''}
                onChange={(e) => handleChange('smsLoginId', e.target.value)}
                disabled={!config?.smsEnabled}
                style={inputStyle}
                placeholder="923XXXXXXXXX"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                Login Password
              </label>
              <input
                type="password"
                value={config?.smsLoginPass || ''}
                onChange={(e) => handleChange('smsLoginPass', e.target.value)}
                disabled={!config?.smsEnabled}
                style={inputStyle}
                placeholder="Enter new password or leave masked"
              />
            </div>

            {config?.smsProvider === 'other' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                  API URL
                </label>
                <input
                  type="text"
                  value={config?.smsApiUrl || ''}
                  onChange={(e) => handleChange('smsApiUrl', e.target.value)}
                  disabled={!config?.smsEnabled}
                  style={inputStyle}
                  placeholder="https://api.example.com/send"
                />
              </div>
            )}
          </div>
        </div>

        {/* ========== PORTAL URL ========== */}
        <div style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 16px', color: '#1f2937', fontSize: 20 }}>🌐 Portal URL</h3>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
              Portal Access Link (used in emails)
            </label>
            <input
              type="url"
              value={config?.portalUrl || ''}
              onChange={(e) => handleChange('portalUrl', e.target.value)}
              style={inputStyle}
              placeholder="http://localhost:3000"
            />
          </div>
        </div>

        {/* ========== SAVE BUTTON ========== */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            type="button"
            onClick={fetchConfig}
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#059669',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
            }}
          >
            {saving ? 'Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.2s',
};
